import { useState } from "react";
import { FileText, Plus, Utensils } from "lucide-react";
import { Button, Field, EmptyState, Spinner, Icon } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useCapability } from "../../app/bandContext.js";
import { useLiveGroup } from "../../app/realtime/RealtimeProvider.js";
import {
  useDos,
  useDosFood,
  useAteHere,
  useDosPdf,
} from "../../app/hooks/useDayOfShow.js";
import { ApiError } from "../../app/hooks/apiError.js";
import { MyBunkCard } from "../buses/MyBunkCard.js";
import styles from "./DayOfShowSection.module.scss";

type WeatherBlock = components["schemas"]["WeatherBlock"];
type ScheduleItemDto = components["schemas"]["ScheduleItemDto"];
type SetlistItemDto = components["schemas"]["SetlistItemDto"];
type FoodItem = components["schemas"]["FoodItem"];

/**
 * The on-screen Day-of-Show surface on the show preview — COMPOSED from the show record
 * (run of show + setlist + weather), never re-entered. Money-free. Joins `show:{id}` for the show's live
 * signals; the printable PDF opens a short-lived signed URL. The food finder degrades to community recs when
 * Places is unavailable. Tickets sold are the settlement door headcount (ART-252), not a log here.
 */
export function DayOfShowSection({ showId }: { showId: string }) {
  useLiveGroup(`show:${showId}`);
  const canWrite = useCapability("schedule:write");
  const dos = useDos(showId);

  if (dos.isLoading) {
    return (
      <section className={styles.section} aria-busy>
        <div className={styles.center}><Spinner label="Loading day-of-show" /></div>
      </section>
    );
  }
  if (dos.isError || !dos.data?.show) {
    return (
      <section className={styles.section}>
        <EmptyState
          title="Couldn't load the day-of-show"
          description={dos.error instanceof ApiError ? dos.error.message : "Try again in a moment."}
        />
      </section>
    );
  }

  const v = dos.data;
  const city = v.show!.city ?? "";

  return (
    <section className={styles.section} data-testid="day-of-show">
      <div className={styles.head}>
        <h3 className={styles.headTitle}>Day of Show</h3>
        <PdfButton showId={showId} />
      </div>

      <div className={styles.cards}>
        <WeatherCard weather={v.weather ?? null} />
        <RunOfShowCard items={v.runOfShow ?? []} />
        <SetlistCard items={v.setlist ?? []} />
        <FoodCard showId={showId} city={city} canWrite={canWrite} />
        {/* PRO: the caller's own bunk (ART-147 §7.5). Hidden when not bunked — a DoS with no bunk just omits it. */}
        <MyBunkCard emptyMode="hide" />
      </div>
    </section>
  );
}

function PdfButton({ showId }: { showId: string }) {
  const pdf = useDosPdf();
  return (
    <Button
      variant="ghost"
      size="sm"
      loading={pdf.isPending}
      onClick={() =>
        pdf.mutate(
          { showId },
          { onSuccess: (link) => link.url && window.open(link.url, "_blank", "noopener,noreferrer") },
        )
      }
    >
      <Icon icon={FileText} size={16} /> Print / PDF
    </Button>
  );
}

function WeatherCard({ weather }: { weather: WeatherBlock | null }) {
  const hasForecast = weather && weather.tempHighC != null;
  return (
    <div className={styles.card} data-testid="dos-weather">
      <h3 className={styles.cardTitle}>Weather</h3>
      {!hasForecast ? (
        <p className={styles.empty}>Forecast unavailable.</p>
      ) : (
        <div className={styles.weather}>
          <span className={styles.weatherTemp}>
            {Math.round(weather.tempHighC!)}° / {Math.round(weather.tempLowC ?? weather.tempHighC!)}°C
          </span>
          <span className={styles.weatherMeta}>
            {weather.summary}
            {weather.precipProbabilityPct != null ? ` · ${weather.precipProbabilityPct}% precip` : ""}
          </span>
        </div>
      )}
    </div>
  );
}

function RunOfShowCard({ items }: { items: readonly ScheduleItemDto[] }) {
  return (
    <div className={styles.card}>
      <h3 className={styles.cardTitle}>Run of Show</h3>
      {items.length === 0 ? (
        <p className={styles.empty}>No run of show yet.</p>
      ) : (
        <ul className={styles.rows}>
          {items.map((i, idx) => (
            <li key={i.itemId ?? idx} className={styles.row}>
              <span className={styles.rowTime}>{i.startsAt ?? "—"}</span>
              <span className={styles.rowLabel}>{i.label || (i.section ?? "").replace(/_/g, " ")}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SetlistCard({ items }: { items: readonly SetlistItemDto[] }) {
  return (
    <div className={styles.card}>
      <h3 className={styles.cardTitle}>Setlist</h3>
      {items.length === 0 ? (
        <p className={styles.empty}>No setlist yet.</p>
      ) : (
        <ol className={styles.setlist}>
          {items.map((s, idx) => (
            // The per-song note ("acoustic", "drop D") is the whole reason a member reads the set here
            // rather than off the editor — the reference prints it beside the title, so it goes on the row.
            <li key={s.itemId ?? idx}>
              <span className={styles.setlistTitle}>{s.title}</span>
              {s.notes && <span className={styles.setlistNote}>{s.notes}</span>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function FoodCard({ showId, city, canWrite }: { showId: string; city: string; canWrite: boolean }) {
  const [open, setOpen] = useState(false);
  const food = useDosFood(showId, open);
  const ateHere = useAteHere();
  const [name, setName] = useState("");

  const items: readonly FoodItem[] = food.data?.items ?? [];

  return (
    <div className={styles.card} data-testid="dos-food">
      <h3 className={styles.cardTitle}>Food</h3>
      {!open ? (
        <Button variant="ghost" size="sm" onClick={() => setOpen(true)} disabled={!city}>
          <Icon icon={Utensils} size={16} /> {city ? `Find food near ${city}` : "No city set"}
        </Button>
      ) : food.isLoading ? (
        <div className={styles.center}><Spinner /></div>
      ) : food.isError || !food.data ? (
        <EmptyState title="Couldn't load food" description="Try again in a moment." />
      ) : (
        <>
          {!food.data.placesAvailable && (
            <p className={styles.degraded} data-testid="dos-food-degraded">
              Live search is unavailable — showing community recommendations.
            </p>
          )}
          {items.length === 0 ? (
            <p className={styles.empty}>No spots yet — be the first to add one.</p>
          ) : (
            <ul className={styles.foodList}>
              {items.map((f, i) => (
                <li key={`${f.name}-${i}`} className={styles.foodItem}>
                  <span className={styles.foodName}>{f.name}</span>
                  {f.address && <span className={styles.foodAddr}>{f.address}</span>}
                  <span className={styles.foodSource} data-source={f.source}>{f.source}</span>
                </li>
              ))}
            </ul>
          )}

          {canWrite && city && (
            <div className={styles.ateForm}>
              <Field
                label="Ate here"
                placeholder="Restaurant name"
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
              />
              <Button
                size="sm"
                loading={ateHere.isPending}
                disabled={name.trim() === ""}
                onClick={() =>
                  ateHere.mutate(
                    { showId, body: { name: name.trim(), city } },
                    { onSuccess: () => setName("") },
                  )
                }
              >
                <Icon icon={Plus} size={16} /> Add
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
